import type { SchemaInput, SchemaRecord } from '../shared/schema-input.js';
import { isNumber, isObject, isString } from '../shared/runtime-values.js';
import { catch as catchSchema, object, optional, unknown as unknownSchema } from 'zod/mini';
import type { infer as Infer } from 'zod/mini';
import { photonReverse, photonSearch, NormalizedFeature } from './photon.js';
import { GeocoderError, type GeocoderProvider } from './geocoder-error.js';
import { buildGeocoderPresentation } from '../shared/geocoder-presentation.js';
import { isGeocodeQueryTooLong } from '../shared/geocoder-limits.js';

export type { NormalizedFeature };

export interface GeocoderOutcome {
  geocoderProvider: GeocoderProvider;
  geocoderFallbackFrom?: GeocoderProvider;
  geocoderFallbackTrigger?: NormalizedFeature['geocoderFallbackTrigger'];
}

let lastNominatimCall = 0;
let nominatimSlotQueue: Promise<void> = Promise.resolve();
let reversePhotonQueue: Promise<void> = Promise.resolve();

const RC_KEY = 'drawonamap:revgeo';
const REVERSE_CACHE_MAX_ENTRIES = 64;
const REVERSE_CACHE_TTL_MS = 60 * 60 * 1000;
const NOMINATIM_MIN_SPACING_MS = 1100;
const NOMINATIM_MAX_RETRY_AFTER_MS = 5000;

interface ReverseCacheEntry {
  feature: NormalizedFeature;
  expiresAt: number;
}

type GeocodeSearchOptions = {
  lat?: number;
  lon?: number;
  lang?: string;
  osm_tag?: string;
  signal?: AbortSignal;
  allowNominatimFallback?: boolean;
  onOutcome?: (outcome: GeocoderOutcome) => void;
};

type ReverseGeocodeFailureReporter = (props: Record<string, string>, event?: 'reverse_geocode_failed' | 'reverse_geocode_outcome') => void;
let reverseGeocodeFailureReporter: ReverseGeocodeFailureReporter | null = null;

export function setReverseGeocodeFailureReporter(reporter: ReverseGeocodeFailureReporter): void {
  reverseGeocodeFailureReporter = reporter;
}

function reportReverseGeocodeFailure(reason: string, fallbackTrigger: string, cause: unknown): void {
  const message = cause instanceof Error ? cause.message : String(cause);
  console.warn('Reverse geocoding failed:', message);
  try {
    reverseGeocodeFailureReporter?.({
      provider: 'nominatim',
      fallback_from: 'photon',
      fallback_trigger: fallbackTrigger,
      reason,
      error_blob: JSON.stringify({ message }).slice(0, 4096),
    });
  } catch {
    // Analytics must not affect address lookup.
  }
}

function reportReverseOutcome(lookupId: string, provider: string, outcome: string): void {
  try {
    reverseGeocodeFailureReporter?.({ lookup_id: lookupId, provider, outcome }, 'reverse_geocode_outcome');
  } catch {
    // Telemetry must not change the lookup result.
  }
}

interface GeocoderErrorDetail {
  provider: GeocoderProvider;
  fallbackFrom?: GeocoderProvider;
  fallbackTrigger?: NormalizedFeature['geocoderFallbackTrigger'];
}

const nominatimAddressSchema = object({
  city: optional(unknownSchema()),
  town: optional(unknownSchema()),
  village: optional(unknownSchema()),
  municipality: optional(unknownSchema()),
  neighbourhood: optional(unknownSchema()),
  quarter: optional(unknownSchema()),
  suburb: optional(unknownSchema()),
  city_district: optional(unknownSchema()),
  borough: optional(unknownSchema()),
  county: optional(unknownSchema()),
  state: optional(unknownSchema()),
  postcode: optional(unknownSchema()),
  country: optional(unknownSchema()),
  country_code: optional(unknownSchema()),
  road: optional(unknownSchema()),
  house_number: optional(unknownSchema()),
});

const nominatimItemSchema = object({
  lat: optional(unknownSchema()),
  lon: optional(unknownSchema()),
  name: optional(unknownSchema()),
  address: catchSchema(optional(nominatimAddressSchema), undefined),
  display_name: optional(unknownSchema()),
  type: optional(unknownSchema()),
  addresstype: optional(unknownSchema()),
  category: optional(unknownSchema()),
  importance: optional(unknownSchema()),
  place_rank: optional(unknownSchema()),
  osm_type: optional(unknownSchema()),
  osm_id: optional(unknownSchema()),
  boundingbox: optional(unknownSchema()),
  error: optional(unknownSchema()),
});

function stringValue(value: SchemaInput): string | undefined {
  return isString(value) && value.length > 0 ? value : undefined;
}

function geocoderFailureTrigger(cause: unknown): NonNullable<NormalizedFeature['geocoderFallbackTrigger']> {
  return cause instanceof GeocoderError ? cause.failureKind : 'network';
}

function tagResults(
  results: NormalizedFeature[],
  detail: GeocoderErrorDetail
): NormalizedFeature[] {
  return results.map((feature) => ({
    ...feature,
    geocoderProvider: detail.provider,
    geocoderFallbackFrom: detail.fallbackFrom,
    geocoderFallbackTrigger: detail.fallbackTrigger
  }));
}

function finishSearch(
  results: NormalizedFeature[],
  detail: GeocoderErrorDetail,
  onOutcome?: GeocodeSearchOptions['onOutcome'],
): NormalizedFeature[] {
  const outcome = {
    geocoderProvider: detail.provider,
    geocoderFallbackFrom: detail.fallbackFrom,
    geocoderFallbackTrigger: detail.fallbackTrigger,
  };
  try {
    onOutcome?.(outcome);
  } catch {
    // Optional analytics context must never affect search results.
  }
  return tagResults(results, detail);
}

function annotateGeocoderError(cause: unknown, detail: GeocoderErrorDetail): Error {
  const source = cause instanceof Error ? cause : null;
  const annotated = cause instanceof GeocoderError
    ? cause
    : new GeocoderError(source?.message ?? String(cause || 'Geocode failed'), 'network', { cause: cause });
  if (source && !(cause instanceof GeocoderError)) annotated.name = source.name;
  annotated.geocoderProvider = detail.provider;
  annotated.geocoderFallbackFrom = detail.fallbackFrom;
  annotated.geocoderFallbackTrigger = detail.fallbackTrigger;
  return annotated;
}

function hasHanScript(query: string): boolean {
  return /\p{Script=Han}/u.test(query);
}

export function normalizeHanAddressQuery(query: string): string {
  const compact = query.trim().replace(/\s+/g, ' ');
  if (!hasHanScript(compact)) return compact;
  return compact
    .replace(/(特别行政区|自治区|自治州|省|市|区|县)(?=\p{Script=Han})/gu, '$1 ')
    .replace(/(\p{Script=Han})(?=[0-9０-９])/gu, '$1 ')
    .replace(/\s+/g, ' ')
    .trim();
}

function reverseCacheRead(): Record<string, ReverseCacheEntry> {
  try {
    const raw = sessionStorage.getItem(RC_KEY);
    if (!raw) return {};
    // SAFETY: The following parser checks every field that it accepts from this external payload.
    const parsed = JSON.parse(raw) as SchemaRecord;
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, ReverseCacheEntry] => {
        // SAFETY: The adjacent object and field checks validate this boundary value before it is accepted.
        const value = entry[1] as Partial<ReverseCacheEntry> | null;
        return value !== null
          && isObject(value)
          && isNumber(value.expiresAt)
          && value.expiresAt > now
          && value.feature !== null
          && isObject(value.feature);
      }),
    );
  } catch {
    return {};
  }
}

function reverseCacheWrite(cache: Record<string, ReverseCacheEntry>) {
  try {
    const bounded = Object.fromEntries(
      Object.entries(cache)
        .sort(([, left], [, right]) => right.expiresAt - left.expiresAt)
        .slice(0, REVERSE_CACHE_MAX_ENTRIES),
    );
    sessionStorage.setItem(RC_KEY, JSON.stringify(bounded));
  } catch {}
}

function reverseCacheKey(lat: number, lon: number, lang?: string): string {
  const rLat = lat.toFixed(5);
  const rLon = lon.toFixed(5);
  return `${rLat},${rLon},${lang?.toLowerCase() ?? 'default'}`;
}

function queuedPhotonReverse(lat: number, lon: number, options?: { lang?: string }): Promise<NormalizedFeature[]> {
  const request = reversePhotonQueue.then(() => photonReverse(lat, lon, options));
  reversePhotonQueue = request.then(() => undefined, () => undefined);
  return request;
}

type InferAddress = NonNullable<Infer<typeof nominatimAddressSchema>>;

function nominatimDistrict(address: InferAddress) {
  return address.neighbourhood
    ?? address.quarter
    ?? address.suburb
    ?? address.city_district
    ?? address.borough;
}

function nominatimExtent(value: SchemaInput): [number, number, number, number] | undefined {
  if (!Array.isArray(value) || value.length !== 4) return undefined;
  const [south, north, west, east] = value.map(Number);
  return [south, north, west, east].every(Number.isFinite)
    ? [west, north, east, south]
    : undefined;
}

function optionalNumber(value: SchemaInput): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function osmId(value: SchemaInput): string | number | undefined {
  return isString(value) || isNumber(value) ? value : undefined;
}

function normalizeNominatim(item: SchemaInput): NormalizedFeature | null {
  const result = nominatimItemSchema.safeParse(item);
  if (!result.success) return null;
  const parsed = result.data;
  const lat = Number(parsed.lat);
  const lon = Number(parsed.lon);
  const address = parsed.address;
  const presentation = buildGeocoderPresentation({
    name: parsed.name,
    street: address?.road,
    housenumber: address?.house_number,
    city: address?.city,
    town: address?.town,
    village: address?.village,
    municipality: address?.municipality,
    district: address ? nominatimDistrict(address) : undefined,
    county: address?.county,
    state: address?.state,
    postcode: address?.postcode,
    country: address?.country,
    fallbackLabel: parsed.display_name,
  });
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || !presentation) return null;

  const extent = nominatimExtent(parsed.boundingbox);
  const normalizedProperties: NormalizedFeature['properties'] = {
    lat,
    lon,
    formatted: presentation.formatted,
    type: stringValue(parsed.addresstype) ?? stringValue(parsed.type),
    extent,
  };
  const category = stringValue(parsed.type) ?? stringValue(parsed.category);
  const postcode = stringValue(address?.postcode);
  const countryCode = stringValue(address?.country_code);
  const parsedOsmType = stringValue(parsed.osm_type);
  const parsedOsmId = osmId(parsed.osm_id);
  const importance = optionalNumber(parsed.importance);
  const placeRank = optionalNumber(parsed.place_rank);
  Object.assign(normalizedProperties, Object.fromEntries([
    ['category', category],
    ['postcode', postcode],
    ['countryCode', countryCode],
    ['osmType', parsedOsmType],
    ['osmId', parsedOsmId],
    ['importance', importance],
    ['placeRank', placeRank],
  ].filter((entry) => entry[1] !== undefined)));
  return {
    properties: normalizedProperties,
    autocompletePresentation: {
      primary: presentation.primary,
      secondary: presentation.secondary,
    },
  };
}

async function waitForNominatimSlot(signal?: AbortSignal, minimumSpacingMs = NOMINATIM_MIN_SPACING_MS): Promise<void> {
  const slot = nominatimSlotQueue.then(async () => {
    if (signal?.aborted) {
      throw signal.reason instanceof Error
        ? signal.reason
        : new DOMException('The operation was aborted.', 'AbortError');
    }
    const delay = Math.max(0, minimumSpacingMs - (Date.now() - lastNominatimCall));
    if (delay > 0) {
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          signal?.removeEventListener('abort', abort);
          resolve();
        }, delay);
        const abort = () => {
          clearTimeout(timeout);
          reject(signal?.reason instanceof Error
            ? signal.reason
            : new DOMException('The operation was aborted.', 'AbortError'));
        };
        signal?.addEventListener('abort', abort, { once: true });
      });
    }
    lastNominatimCall = Date.now();
  });
  nominatimSlotQueue = slot.then(() => undefined, () => undefined);
  await slot;
}

function nominatimRetrySpacing(response: Response): number {
  const retryAfterSeconds = Number(response.headers.get('retry-after'));
  if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) return NOMINATIM_MIN_SPACING_MS;
  return Math.min(
    NOMINATIM_MAX_RETRY_AFTER_MS,
    Math.max(NOMINATIM_MIN_SPACING_MS, Math.ceil(retryAfterSeconds * 1000) + 100),
  );
}

async function fetchNominatim(url: URL, signal?: AbortSignal): Promise<Response> {
  await waitForNominatimSlot(signal);
  const first = await fetch(url, { signal });
  if (first.status !== 429) return first;

  const retrySpacing = nominatimRetrySpacing(first);
  await first.body?.cancel();
  await waitForNominatimSlot(signal, retrySpacing);
  return fetch(url, { signal });
}

async function nominatimSearch(
  query: string,
  limit: number,
  options?: { lang?: string; signal?: AbortSignal }
): Promise<NormalizedFeature[]> {
  const params = new URLSearchParams({
    q: query,
    format: 'jsonv2',
    addressdetails: '1',
    limit: String(limit),
  });
  if (options?.lang) params.set('accept_language', options.lang);
  const origin = !('window' in globalThis) ? 'https://drawonamap.com' : window.location.origin;
  const url = new URL('/api/geocode', origin);
  url.searchParams.set('provider', 'nominatim');
  for (const [key, value] of params) url.searchParams.set(key, value);
  const res = await fetchNominatim(url, options?.signal);
  if (!res.ok) {
    throw new GeocoderError(`Nominatim geocode failed: ${res.status}`, 'http', { response: res });
  }
  const data = await res.json();
  if (!Array.isArray(data)) return [];
  return data.map(normalizeNominatim).filter((feature): feature is NormalizedFeature => !!feature);
}

async function nominatimReverse(lat: number, lon: number, lang?: string): Promise<NormalizedFeature | null> {
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    format: 'jsonv2',
    zoom: '18',
    addressdetails: '1',
  });
  if (lang) params.set('accept_language', lang);
  const origin = !('window' in globalThis) ? 'https://drawonamap.com' : window.location.origin;
  const url = new URL('/api/geocode', origin);
  url.searchParams.set('provider', 'nominatim');
  url.searchParams.set('mode', 'reverse');
  for (const [key, value] of params) url.searchParams.set(key, value);
  const res = await fetchNominatim(url);
  if (!res.ok) {
    throw new GeocoderError(`Nominatim reverse geocode failed: ${res.status}`, 'http', { response: res });
  }
  const item = await res.json();
  const parsed = nominatimItemSchema.safeParse(item);
  if (!parsed.success || parsed.data.error) return null;
  return normalizeNominatim(parsed.data);
}

function photonSearchDetail(results: NormalizedFeature[]): GeocoderErrorDetail {
  return results[0]?.geocoderProvider === 'geoapify'
    ? { provider: 'geoapify', fallbackFrom: 'photon', fallbackTrigger: 'http' }
    : { provider: 'photon' };
}

export async function geocodeSearch(query: string, limit = 5, options?: GeocodeSearchOptions): Promise<NormalizedFeature[]> {
  if (isGeocodeQueryTooLong(query)) return [];
  const shouldFallbackToNominatim = options?.allowNominatimFallback !== false;
  try {
    const photonResults = await photonSearch(query, limit, options);
    if (photonResults.length > 0 || !shouldFallbackToNominatim) {
      return finishSearch(photonResults, photonSearchDetail(photonResults), options?.onOutcome);
    }
    const fallbackDetail: GeocoderErrorDetail = {
      provider: 'nominatim',
      fallbackFrom: 'photon',
      fallbackTrigger: 'empty_results'
    };
    const nominatimResults = await nominatimSearch(query, limit, options);
    if (nominatimResults.length > 0) {
      return finishSearch(nominatimResults, fallbackDetail, options?.onOutcome);
    }

    const normalizedQuery = normalizeHanAddressQuery(query);
    if (normalizedQuery === query.trim()) {
      return finishSearch(nominatimResults, fallbackDetail, options?.onOutcome);
    }
    try {
      const normalizedResults = await photonSearch(normalizedQuery, limit, options);
      return finishSearch(normalizedResults, {
        provider: 'photon',
        fallbackFrom: 'nominatim',
        fallbackTrigger: 'empty_results',
      }, options?.onOutcome);
    } catch {
      return finishSearch(nominatimResults, fallbackDetail, options?.onOutcome);
    }
  } catch (error) {
    // SAFETY: The local owner contract defines this value with the stated concrete type.
    if (!shouldFallbackToNominatim || (error as Error)?.name === 'AbortError') {
      throw annotateGeocoderError(error, { provider: 'photon' });
    }
    try {
      const fallbackDetail: GeocoderErrorDetail = {
        provider: 'nominatim',
        fallbackFrom: 'photon',
        fallbackTrigger: geocoderFailureTrigger(error)
      };
      return finishSearch(await nominatimSearch(query, limit, options), fallbackDetail, options?.onOutcome);
    } catch (fallbackError) {
      throw annotateGeocoderError(fallbackError, {
        provider: 'nominatim',
        fallbackFrom: 'photon',
        fallbackTrigger: geocoderFailureTrigger(error)
      });
    }
  }
}

export async function reverseGeocode(lat: number, lon: number, options?: { lang?: string }): Promise<NormalizedFeature | null> {
  const key = reverseCacheKey(lat, lon, options?.lang);
  const cached = reverseCacheRead();
  if (key in cached) return cached[key].feature;

  const lookupId = crypto.randomUUID();
  let feature: NormalizedFeature | null = null;
  let fallbackTrigger: NonNullable<NormalizedFeature['geocoderFallbackTrigger']> = 'empty_results';
  try {
    const [photonFeature] = await queuedPhotonReverse(lat, lon, options);
    reportReverseOutcome(lookupId, 'photon', photonFeature ? 'success' : 'empty_results');
    feature = photonFeature
      ? { ...photonFeature, geocoderProvider: 'photon' as const }
      : null;
  } catch (error) {
    fallbackTrigger = geocoderFailureTrigger(error);
    reportReverseOutcome(lookupId, 'photon', fallbackTrigger);
  }

  if (!feature) {
    try {
      const fallbackFeature = await nominatimReverse(lat, lon, options?.lang);
      reportReverseOutcome(lookupId, 'nominatim', fallbackFeature ? 'success' : 'empty_results');
      feature = fallbackFeature
        ? {
            ...fallbackFeature,
            geocoderProvider: 'nominatim',
            geocoderFallbackFrom: 'photon',
            geocoderFallbackTrigger: fallbackTrigger,
          }
        : null;
    } catch (error) {
      reportReverseOutcome(lookupId, 'nominatim', geocoderFailureTrigger(error));
      reportReverseGeocodeFailure(geocoderFailureTrigger(error), fallbackTrigger, error);
      return null;
    }
  }

  try {
    if (!feature) {
      reportReverseGeocodeFailure('empty_results', fallbackTrigger, new Error('Both reverse geocoders returned no result'));
      return null;
    }
    const updatedCache = reverseCacheRead();
    updatedCache[key] = {
      feature,
      expiresAt: Date.now() + REVERSE_CACHE_TTL_MS,
    };
    reverseCacheWrite(updatedCache);
    return feature;
  } catch (error) {
    console.warn('Reverse geocode cache write failed:', error);
    return feature;
  }
}
